/*
 * Copyright ish group pty ltd. All rights reserved. https://www.ish.com.au
 * No copying or use of this code is allowed without permission in writing from ish.
 */

import { Amount, Contact } from '@api';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import classnames from 'classnames';
import React, { useEffect, useState } from 'react';
import { Dispatch } from 'redux';
import { IAction } from 'src/js/actions/IshAction';
import { getCheckoutModelFromBackend, showSyncErrors } from 'src/js/enrol/actions/Actions';
import { resetPaymentState, setPaymentFetching } from 'src/js/enrol/containers/payment/actions/Actions';
import CheckoutApiService from 'src/js/enrol/services/CheckoutApiService';


interface Props {
  contacts: Contact[];
  amount: Amount;
  dispatch: Dispatch<IAction>;
  onSetPayer?: (id: string) => void;
  onAddPayer?: () => any;
  onAddCompany?: () => any;
  payerId?: string;
  disabled?: boolean;
  checkoutProcessStripeCCPayment: (stripePaymentMethodId: string, stripe: Stripe) => void;
}

const StripePaymentForm = ({ disabled, email, handleError, setLoading, checkoutProcessStripeCCPayment }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [ready, setReady] = useState(false);

  const handleSubmit = async event => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);

    const { error: submitError } = await elements.submit();

    if (submitError) {
      handleError(submitError);
      return;
    }

    const { paymentMethod, error } = await stripe.createPaymentMethod({
      elements,
      params: {
        billing_details: {
          email
        }
      }
    });

    if (error) {
      handleError(error);
      return;
    }

    checkoutProcessStripeCCPayment(paymentMethod.id, stripe);
  };

  return <>
    <div className="form-group">
      <PaymentElement onReady={() => setReady(true)} />
    </div>
    <button
      disabled={!ready || !stripe || disabled}
      className={classnames('btn btn--primary', { disabled })}
      id="enterCardDetails"
      name="enterCardDetails"
      onClick={handleSubmit}
    >
      Finalize checkout
    </button>
  </>;
};

const StripePaymentComp = (props: Props) => {
  const {
    contacts,
    amount,
    payerId,
    dispatch,
    disabled,
    checkoutProcessStripeCCPayment
  } = props;

  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null>>(null);

  useEffect(() => {
    CheckoutApiService.getClientPreferences()
      .then(res => setStripePromise(loadStripe(res.clientKey)))
  }, []);

  const email = contacts.find(c => c.id === payerId)?.email;

  const setLoading = (loading: boolean) => {
    dispatch(setPaymentFetching(loading));
  };

  const proceedPayment = () => {
    dispatch(resetPaymentState());
    dispatch(getCheckoutModelFromBackend());
  };

  const handleError = error => {
    setLoading(false);
    dispatch(showSyncErrors({ message: error?.message }));
  };

  useEffect(() => {
    if (amount.payNow > 0) {
      proceedPayment();
    }
  }, [
    amount.payNow
  ]);

  return (stripePromise && amount.payNow > 0 &&
    <Elements
      stripe={stripePromise}
      options={{
        mode: 'payment',
        currency: 'aud',
        paymentMethodCreation: 'manual',
        payment_method_types: ['card'],
        amount: amount.payNow * 100
      }}
    >
      <StripePaymentForm
        disabled={disabled}
        checkoutProcessStripeCCPayment={checkoutProcessStripeCCPayment}
        handleError={handleError}
        setLoading={setLoading}
        email={email}
      />
    </Elements>
  );
};

export default StripePaymentComp;